import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { MyThunkDispatch } from 'store';

import { addressesEdit } from 'action-creators/addresses';
import Header from 'components/AppBar';
import Button from 'components/Buttons/Button';
import Loader from 'components/Loader';
import { Desktop, Mobile } from 'components/Responsive';
import ScreenHeader from 'components/ScreenHeader';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { Company } from 'models/company';
import CompanyProfileSuccess from 'screens/components/CompanyProfileSuccess';

import Info from './components/Info';
import { Container, Content, HeaderContainer, HeaderTitle, Title, Group, EditIcon, ButtonContainer } from './styles';

interface Props {
  isLoading: boolean;
  company?: Company | null;
}

const CompanyBilling = ({ isLoading, company }: Props) => {
  const [showSuccess, setShowSuccess] = useState(false);

  const history = useHistory<{ completingCompanyProfile?: boolean; paymentCompleted?: boolean }>();
  const { t } = useStoreTranslation();
  const dispatch: MyThunkDispatch = useDispatch();

  const isCompletingCompanyProfile = !!history.location.state?.completingCompanyProfile;
  const isPaymentCompleted = !!history.location.state?.paymentCompleted;

  const country = t(`countries.${company?.countryId}`);
  const name = company?.billingInformation?.name || '';
  const last_name = company?.billingInformation?.last_name || '';
  const contactName = `${name} ${last_name}`;

  return (
    <>
      {isLoading || !company ? (
        <Loader fullScreen />
      ) : (
        <Container>
          <Desktop>
            <ScreenHeader title={t('billing.profile.title')} />
          </Desktop>

          <Mobile>
            <Header goBack={() => history.push('/dashboard')}>
              <HeaderContainer>
                <HeaderTitle>{t('billing.profile.title')}</HeaderTitle>
              </HeaderContainer>
            </Header>
          </Mobile>

          <Content>
            <Group>
              <Title>
                {t('billing.detail.title')}
                <EditIcon onClick={() => history.push('/companyBillingEdit')} />
              </Title>
              <Info icon="/images/billing/company.svg" title={t('billing.detail.name')} info={company?.name} />
              <Info icon="/images/billing/country.svg" title={t('billing.detail.country.label')} info={country} />
              <Info
                icon="/images/billing/document.svg"
                title={t('billing.detail.document.label')}
                info={company?.document}
              />
              <Info
                icon="/images/profile-icons/letter.svg"
                title={t('billing.detail.email')}
                info={company?.billingInformation?.billing_email}
              />
            </Group>
            <Group>
              <Title>{t('billing.contact.title')}</Title>
              <Info icon="/images/profile-icons/circle.svg" title={t('billing.contact.name')} info={contactName} />
              <Info
                icon="/images/profile-icons/mobile-chat.svg"
                title={t('billing.contact.phone')}
                info={company?.billingInformation?.phone_number}
              />
              <Info
                icon="/images/profile-icons/letter.svg"
                title={t('billing.contact.email')}
                info={company?.billingInformation?.email}
              />
            </Group>
            <Group>
              <Title>
                {t('billing.map.address')}
                <EditIcon
                  onClick={() => {
                    const address = company?.address?.address;
                    if (address) {
                      dispatch(addressesEdit(address));
                    }
                    history.push('/companyBillingAddressEdit');
                  }}
                />
              </Title>
              <Info icon="/images/billing/location.svg" title={t('billing.map.address')}>
                <>
                  {company.address?.address} <br />
                  {company.address?.description} <br />
                  {company.address?.zip_code} <br />
                  {company.address?.country ? t(`countries.${company.address.country}`) : null}
                </>
              </Info>
            </Group>
          </Content>

          {isCompletingCompanyProfile && (
            <ButtonContainer>
              <Button
                onClick={() => {
                  if (isPaymentCompleted) setShowSuccess(true);
                  else history.push('/paymentMethods', history.location.state);
                }}
              >
                {t('billing.profile.buttonSave')}
              </Button>
            </ButtonContainer>
          )}
        </Container>
      )}

      <CompanyProfileSuccess show={showSuccess} />
    </>
  );
};

export default CompanyBilling;
